import styled from '@emotion/styled/macro';
import React, { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { FlexWrapper } from '../../theme/common_style';
import { Collapse, message, Skeleton } from 'antd';
import Title from 'antd/es/typography/Title';
import PropTypes from 'prop-types';
import GraphLoader from '../loaders/GraphLoader';
import { currency } from '../../utils/constant';

const colors = [
  '#A8A1FF',
  '#97DBFF',
  '#FFC881',
  '#C58AFF',
  '#71AFFF',
  '#82ca9d',
  '#FFA3B3',
  '#FFD36E'
];

// Helper function to format large numbers
const formatNumber = (value) => {
  if (value >= 1000000000) {
    return (value / 1000000000).toFixed(1) + 'B';
  } else if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'K';
  }
  return value.toString();
};

// Helper function to format numbers with commas
const formatNumberWithCommas = (value) => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Helper function to break long text into multiple lines
const breakLongText = (text, maxLength = 15) => {
  if (text.length <= maxLength) return [text];

  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    if ((currentLine + word).length <= maxLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine) lines.push(currentLine);
  return lines;
};

// Custom X-axis tick component
const CustomXAxisTick = (props) => {
  const { x, y, payload } = props;

  const staticLabel = {
    'Quality Assurance & Testing': 'QA Team',
    'UI/UX Design': 'Graphics Team',
    'Business Development': 'Business Team',
    'Human Resources & Accounts & Finance': 'HRM',
    'Project & Product Management': 'PM Team'
  };

  const lines = breakLongText(staticLabel[payload.value] || payload.value);

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, index) => (
        <text
          key={index}
          x={0}
          y={index * 12 + 10}
          dy={0}
          textAnchor="middle"
          fill="#666"
          fontSize="12">
          {line}
        </text>
      ))}
    </g>
  );
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
        <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{label}</p>
        <p style={{ margin: 0, color: payload[0].color }}>
          {`Value: ${formatNumberWithCommas(payload[0].value)}`}
        </p>
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string
};

CustomXAxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.object
};

const BarGraph = ({ title, api, sign = '' }) => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [showNumber, setShowNumber] = useState(false);

  const handleGetGraphData = async () => {
    try {
      setLoading(true);
      let res = await api();

      if (res?.statusCode === 200) {
        const data = Object?.entries(res?.data?.records || {})?.map(([key, value]) => ({
          name: key,
          count: value
        }));
        setRecords(data);
        setTotal(res?.data?.total);
      } else {
        message.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      message.error(error?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    handleGetGraphData();
  }, []);
  const items = [
    {
      key: 1,
      label: (
        <FlexWrapper direction="column" align="start" justify="start" width="100%">
          <p style={{ color: '#767676', margin: 0 }}>{title}</p>
          {sign == currency.INR ? (
            <Title
              level={4}
              style={{ margin: 0, cursor: 'pointer', minWidth: '100px', textAlign: 'left' }}
              onMouseEnter={() => setShowNumber(true)}
              onMouseLeave={() => setShowNumber(false)}>
              {loading ? (
                <Skeleton.Input active={true} />
              ) : showNumber ? (
                sign + formatNumberWithCommas(total)
              ) : (
                Array(7)
                  .fill('')
                  .map(() => ' *')
              )}
            </Title>
          ) : (
            <Title level={4} style={{ margin: 0 }}>
              {loading ? <Skeleton.Input active={true} /> : sign + formatNumberWithCommas(total)}
            </Title>
          )}
        </FlexWrapper>
      ),
      children: (
        <>
          {loading ? (
            <GraphLoader type="line" height={'360px'} />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={records || []}>
                <CartesianGrid strokeDasharray="1" vertical={false} horizontal={true} />
                <XAxis
                  dataKey={'name'}
                  axisLine={false}
                  tickLine={false}
                  tick={<CustomXAxisTick />}
                  interval={0}
                  height={70}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                  tickFormatter={formatNumber}
                  domain={[0, 'dataMax']}
                />
                <Tooltip cursor={{ fill: 'transparent' }} content={<CustomTooltip />} />
                <Bar dataKey={'count'} fill="#8884d8" barSize={40} radius={[4, 4, 0, 0]}>
                  {(records || [])?.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </>
      )
    }
  ];
  return (
    <ContentWrapper>
      <Collapse items={items} defaultActiveKey={[]} prefixCls="antCustomCollapse" />
    </ContentWrapper>
  );
};

export default BarGraph;

const ContentWrapper = styled(FlexWrapper)`
  width: 100%;
`;

BarGraph.propTypes = {
  title: PropTypes.string,
  api: PropTypes.any,
  sign: PropTypes.string
};
